import React, { useEffect, useRef, useState } from "react";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Robot, X, PaperPlaneTilt, CircleNotch, Trash, Sparkle, CheckCircle, Lightning, Warning } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "Find loads leaving Dallas tomorrow",
  "Show overdue invoices",
  "Recruit owner operators within 100 miles",
  "Generate today's revenue report",
  "Who are my available Reefer drivers?",
];

const AGENT_LABEL = {
  load_sourcing: "Load Sourcing",
  dispatch_matching: "Dispatch Matching",
  tracking_alert: "Tracking & Alerts",
  negotiation: "Negotiation",
  carrier_recruitment: "Carrier Recruitment",
  document_processing: "Document / Compliance",
  billing_invoicing: "Billing & Invoicing",
  performance_ranking: "Performance & Ranking",
};

const AGENT_COLOR = {
  load_sourcing: "#A78BFA",
  dispatch_matching: "#E50914",
  tracking_alert: "#F97316",
  negotiation: "#FCD34D",
  carrier_recruitment: "#16A34A",
  document_processing: "#60A5FA",
  billing_invoicing: "#3B82F6",
  performance_ranking: "#EC4899",
};

function PlanCard({ plan, onApprove, onCancel, busy }) {
  return (
    <div className="rounded border border-yellow-700 bg-yellow-950/30 p-3 text-sm" data-testid="orchestrator-plan-card">
      <div className="flex items-center gap-2 mb-2 font-bold text-yellow-200">
        <Lightning size={14} weight="fill" /> Multi-agent plan — approval required
      </div>
      <div className="space-y-2">
        {plan.map((s, i) => (
          <div key={`${s.agent}-${i}`} className="flex items-start gap-2" data-testid={`plan-step-${i}`}>
            <span className="font-mono text-[10px] text-neutral-500 mt-0.5">#{i + 1}</span>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0"
                  style={{ background: AGENT_COLOR[s.agent] || "#525252", color: "#000" }}>
              {AGENT_LABEL[s.agent] || s.agent}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-mono text-yellow-100">{s.action}</div>
              {s.rationale && <div className="text-[11px] text-neutral-400 mt-0.5">{s.rationale}</div>}
              {Object.keys(s.params || {}).length > 0 && (
                <div className="text-[10px] text-neutral-500 mt-1 font-mono">
                  {Object.entries(s.params).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join("  ·  ")}
                </div>
              )}
              {s.mutates && (
                <div className="inline-flex items-center gap-1 text-[10px] text-red-400 mt-1"><Warning size={10} weight="fill" /> mutates data</div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3 pt-3 border-t border-yellow-900">
        <button onClick={onApprove} disabled={busy} className="rf-btn flex-1 justify-center"
          style={{ background: "#16A34A", color: "#fff" }} data-testid="plan-approve">
          {busy ? <CircleNotch size={12} className="animate-spin" /> : <CheckCircle size={12} weight="bold" />}
          Approve &amp; Execute
        </button>
        <button onClick={onCancel} disabled={busy} className="rf-btn"
          style={{ background: "transparent", color: "#fff", border: "1px solid #525252" }} data-testid="plan-cancel">
          Cancel
        </button>
      </div>
    </div>
  );
}

function ResultBubble({ results }) {
  return (
    <div className="space-y-2" data-testid="orchestrator-results">
      {results.map((r, i) => {
        const ok = r.result?.ok !== false;
        return (
          <div key={`${r.agent}-${i}`} className="rounded border border-neutral-800 p-2 text-xs">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                    style={{ background: AGENT_COLOR[r.agent] || "#525252", color: "#000" }}>
                {AGENT_LABEL[r.agent] || r.agent}
              </span>
              <span className="font-mono text-[10px] text-neutral-500">{r.action}</span>
              <span className="ml-auto" style={{ color: ok ? "#16A34A" : "#E50914" }}>
                {ok ? "OK" : "FAIL"}
              </span>
            </div>
            <div className="text-neutral-200">{r.result?.summary}</div>
            {r.result?.loads && r.result.loads.length > 0 && (
              <div className="mt-2 max-h-32 overflow-y-auto space-y-0.5 font-mono text-[10px] text-neutral-400">
                {r.result.loads.slice(0, 8).map((l, j) => (
                  <div key={j}>• {l.load_number} {l.origin} → {l.destination} · {l.status} · ${l.rate_usd || 0}</div>
                ))}
              </div>
            )}
            {r.result?.drivers && r.result.drivers.length > 0 && (
              <div className="mt-2 max-h-28 overflow-y-auto space-y-0.5 font-mono text-[10px] text-neutral-400">
                {r.result.drivers.slice(0, 8).map((d, j) => (
                  <div key={j}>• {d.name} · {d.equipment_type || "—"} · {d.driver_status || "Available"}</div>
                ))}
              </div>
            )}
            {r.result?.invoices && r.result.invoices.length > 0 && (
              <div className="mt-2 max-h-28 overflow-y-auto space-y-0.5 font-mono text-[10px] text-neutral-400">
                {r.result.invoices.slice(0, 8).map((iv, j) => (
                  <div key={j}>• {iv.invoice_number} · {iv.customer_name} · ${iv.amount_usd} · {iv.status}</div>
                ))}
              </div>
            )}
            {r.result?.target_lanes && r.result.target_lanes.length > 0 && (
              <div className="mt-2 max-h-28 overflow-y-auto space-y-0.5 font-mono text-[10px] text-neutral-400">
                {r.result.target_lanes.slice(0, 8).map((l, j) => (
                  <div key={j}>• {l.origin} → {l.destination} · ${l.rate_usd || 0} @ ${l.rpm || 0}/mi</div>
                ))}
              </div>
            )}
            {r.result?.username && (
              <div className="mt-2 p-2 rounded bg-green-950/40 border border-green-900 text-[11px]">
                <div className="text-green-300 font-bold mb-1">Driver onboarded</div>
                <div className="font-mono">user: <b>{r.result.username}</b></div>
                <div className="font-mono">temp pw: <b>{r.result.temp_password}</b></div>
                <div className="font-mono">dispatcher: {r.result.dispatcher_name}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function CopilotPanel() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null); // {plan_id, plan, summary}
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy, pendingPlan, open]);

  if (!user || !["admin", "dispatcher"].includes(user.role)) return null;

  const ask = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { from: "user", text: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/copilot/orchestrate", { message: msg });
      if (data.kind === "needs_approval") {
        setMessages((m) => [...m, { from: "ai", text: data.summary, plan: data.plan, planId: data.plan_id, kind: "proposed" }]);
        setPendingPlan({ plan_id: data.plan_id, plan: data.plan, summary: data.summary });
      } else if (data.kind === "executed") {
        setMessages((m) => [...m, { from: "ai", text: data.summary, results: data.results, kind: "executed" }]);
        setPendingPlan(null);
      } else {
        setMessages((m) => [...m, { from: "ai", text: data.summary, kind: "informational" }]);
        setPendingPlan(null);
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
      setMessages((m) => [...m, { from: "ai", text: "[error — try again]" }]);
    }
    setBusy(false);
  };

  const approve = async () => {
    if (!pendingPlan) return;
    setBusy(true);
    try {
      const { data } = await api.post("/copilot/execute", { plan_id: pendingPlan.plan_id });
      const status = data.kind === "executed" ? "Executed" : `Aborted (${data.kind})`;
      setMessages((m) => [...m, { from: "ai", text: status, results: data.results, kind: "executed" }]);
      setPendingPlan(null);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  const cancelPlan = async () => {
    if (!pendingPlan) return;
    try { await api.post("/copilot/cancel", { plan_id: pendingPlan.plan_id }); } catch {}
    setMessages((m) => [...m, { from: "ai", text: "Plan cancelled.", kind: "informational" }]);
    setPendingPlan(null);
  };

  const clearHistory = async () => {
    try {
      await api.delete("/copilot/history");
      setMessages([]); setPendingPlan(null);
      toast.success("Chat cleared");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-6 z-50 rounded-full shadow-lg flex items-center gap-2 font-display font-bold text-sm pl-4 pr-5 py-3 transition-transform hover:scale-105"
        style={{ background: "#E50914", color: "#fff", boxShadow: "0 8px 24px rgba(229,9,20,0.45)" }}
        data-testid="ask-ai-button"
      >
        <Sparkle size={16} weight="fill" /> Ask AI
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex justify-end" style={{ background: "rgba(0,0,0,0.55)" }} onClick={() => setOpen(false)}>
          <div
            className="w-full max-w-lg h-full flex flex-col"
            style={{ background: "#0A0A0A", borderLeft: "1px solid #262626" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="copilot-panel"
          >
            <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: "#E50914" }}>
                  <Robot size={16} weight="fill" style={{ color: "#fff" }} />
                </div>
                <div>
                  <div className="font-display font-black text-white">Multi-Agent Orchestrator</div>
                  <div className="label-tag text-neutral-500">8 specialist agents · Claude Sonnet 4.5</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={clearHistory} className="text-neutral-500 hover:text-red-500 p-1" title="Clear chat" data-testid="copilot-clear">
                  <Trash size={16} />
                </button>
                <button onClick={() => setOpen(false)} className="text-neutral-500 hover:text-white p-1" data-testid="copilot-close">
                  <X size={18} />
                </button>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="copilot-messages">
              {messages.length === 0 && (
                <div className="space-y-3">
                  <div className="text-sm text-neutral-400">Ask the orchestrator anything. I'll route it to the right specialist agent, ask for your approval before any mutation, then execute.</div>
                  <div className="space-y-1">
                    <div className="label-tag text-neutral-500">Try</div>
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={() => ask(s)} className="block w-full text-left text-xs text-neutral-300 hover:text-white p-2 rounded border border-neutral-800 hover:border-red-700 transition-colors" data-testid={`copilot-suggestion-${SUGGESTIONS.indexOf(s)}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={`${m.from}-${i}`} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`} data-testid={`copilot-msg-${m.from}-${i}`}>
                  <div
                    className="max-w-[92%] px-3 py-2 rounded text-sm whitespace-pre-wrap"
                    style={{
                      background: m.from === "user" ? "#2563EB" : "#262626",
                      color: m.from === "user" ? "#FFFFFF" : "#F5F5F5",
                      border: m.from === "user" ? "1px solid #3B82F6" : "1px solid #404040",
                      borderLeft: m.from === "ai" ? "3px solid #E50914" : "1px solid #3B82F6",
                      width: m.results || m.plan ? "92%" : "auto",
                    }}
                  >
                    <div style={{ color: "inherit" }}>{m.text}</div>
                    {m.results && <div className="mt-2"><ResultBubble results={m.results} /></div>}
                    {m.plan && m.planId && pendingPlan?.plan_id === m.planId && (
                      <div className="mt-2"><PlanCard plan={m.plan} onApprove={approve} onCancel={cancelPlan} busy={busy} /></div>
                    )}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="px-3 py-2 rounded text-sm" style={{ background: "#262626", color: "#F5F5F5", border: "1px solid #404040", borderLeft: "3px solid #E50914" }}>
                    <CircleNotch size={14} className="animate-spin inline mr-2" />Routing to agents…
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-neutral-800 p-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
                  placeholder="Ask the orchestrator…"
                  rows={2}
                  className="rf-input flex-1 resize-none"
                  data-testid="copilot-input"
                  disabled={busy}
                />
                <button onClick={() => ask()} disabled={busy || !input.trim()} className="rf-btn" style={{ background: "#E50914", color: "#fff" }} data-testid="copilot-send">
                  <PaperPlaneTilt size={14} weight="fill" />
                </button>
              </div>
              <div className="text-[10px] text-neutral-600 mt-1">Enter to send · Shift+Enter for newline</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
